import { useMutation } from '@apollo/client/react';
import { BlobUpload } from '@rails/activestorage/src/blob_upload';
import { FileChecksum } from '@rails/activestorage/src/file_checksum';
import { useState } from 'react';

import { CreateDirectUploadDocument } from '@/graphql/generated/graphql';

interface UploadStateType {
  isUploading: boolean;
  error: string | null;
  uploadedFileUrl?: string | undefined;
  objectKey?: string | undefined;
}

interface FileMetadata {
  checksum: string;
  filename: string;
  contentType: string;
  byteSize: number;
}

const calculateChecksum = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    FileChecksum.create(file, (error, checksum) => {
      if (error) {
        reject(error);
        return;
      }

      resolve(checksum as string);
    });
  });
};

export const getFileMetadata = (file: File): Promise<FileMetadata> => {
  return new Promise((resolve) => {
    calculateChecksum(file).then((checksum) => {
      resolve({
        checksum,
        filename: file.name,
        contentType: file.type,
        byteSize: file.size,
      });
    });
  });
};

// eslint-disable-next-line
const directUpload = (url: string, headers: Record<any, any>, file: File) => {
  const upload = new BlobUpload({ file, directUploadData: { url, headers } });

  return new Promise((resolve, reject) => {
    upload.create((error, blob) => {
      if (error) {
        reject(error);
        return;
      }

      resolve(blob);
    });
  });
};

export const useDirectUpload = () => {
  const [uploadState, setUploadState] = useState<UploadStateType>({
    isUploading: false,
    error: null,
    uploadedFileUrl: undefined,
    objectKey: undefined,
  });
  const [createDirectUpload] = useMutation(CreateDirectUploadDocument);

  /**
   * Uploads a file directly to storage using a presigned URL.
   *
   * @param {File} file - The file to be uploaded.
   * @param {string} [key] - Optional. Hyphenated directory pathname (can be nested) for the file upload.
   *   The `key` parameter represents the directory pathname for the file upload.
   *   It can be a nested path (e.g., "folder/subfolder") and will be used as part of the storage key on the backend.
   *   If omitted, the file will be uploaded to the root directory for the current user's company.
   */
  const uploadFile = async (file: File, key?: string) => {
    setUploadState({ isUploading: true, error: null });

    const metadata = await getFileMetadata(file);

    try {
      const { data } = await createDirectUpload({
        variables: {
          input: {
            ...(key && { key }),
            filename: file.name,
            checksum: metadata.checksum,
            byteSize: String(file.size),
            contentType: file.type,
          },
        },
      });

      if (data?.createDirectUpload) {
        const { url, headers } = data.createDirectUpload;

        await directUpload(url, headers, file);
        setUploadState({ isUploading: false, error: null });

        return {
          id: data.createDirectUpload.id,
          signedBlobId: data.createDirectUpload.signedBlobId,
          previewUrl: URL.createObjectURL(file),
          filename: metadata.filename,
          byteSize: metadata.byteSize,
        };
      }
      // eslint-disable-next-line
    } catch (error: any) {
      setUploadState({ isUploading: false, error: error.message });
    }
  };

  return { ...uploadState, uploadFile };
};

export default useDirectUpload;
